import isEqual from 'lodash/isEqual';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import type {
	StatusFieldChangePayload,
	ResetFormFieldsForCreateAnotherPayload,
	UseFormContextStateChange,
	CREATE_FORM_FIELD_VALUE_CHANGE_PAYLOAD,
	CreateFormFieldConfigUpdatePayload,
	IssueTypeFieldValueChangePayload,
	ProjectFieldValueChangePayload,
	ClassificationLevelFieldChangePayload,
} from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	type Action,
	createContainer,
	createActionsHook,
} from '@atlassian/react-sweet-state';
import type { TriggerIssueCreateModalState } from '../../common/types.tsx';
import type { InProgressIssueService } from '../../services/in-progress-issue/index.tsx';
import type { InProgressIssueControllerState } from './types.tsx';
import { convertStateToInProgressIssueForStorage, isRequestType } from './utils.tsx';

export const initialState: InProgressIssueControllerState = {
	isInitialised: false,
	isSavingCounter: 0,
	id: '',
	formData: {},
	formDataDefaultValues: {},
	formContext: {},
	triggerIssueCreateModalState: undefined,
	statusField: {},
	restoreError: undefined,
};

type ContainerProps = {
	inProgressIssueService: InProgressIssueService;
};

let hasFiredUnexpectedEventErrorAnalytics = false;
// exported to reset tests
export const resetHasFiredUnexpectedEventErrorAnalytics = () => {
	hasFiredUnexpectedEventErrorAnalytics = false;
};
const fireUnexpectedEventErrorAnalytics = (
	eventName: string,
	state: InProgressIssueControllerState,
) => {
	// Prevent errors from firing too often, otherwise we mind fire this on each keypress of field update by accident
	if (hasFiredUnexpectedEventErrorAnalytics) {
		return;
	}
	hasFiredUnexpectedEventErrorAnalytics = true;
	fireErrorAnalytics({
		meta: {
			id: 'useInProgressIssueService',
			packageName: 'jiraIssueCreateRestore',
		},
		attributes: {
			inProgressIssueID: state?.id,
			isModalOpen: state?.triggerIssueCreateModalState?.isModalOpen,
			wasStateRestored: state?.triggerIssueCreateModalState?.wasStateRestored,
			minimizableModalView: state?.triggerIssueCreateModalState?.minimizableModalView,
			eventName,
		},
		error: new Error('InProgressIssue - unexpected event fired'),
		sendToPrivacyUnsafeSplunk: true,
		skipSentry: true,
	});
};

const THROTTLE_DELAY = 250;

/**
 * InProgressIssue contains all the data that is required to restore a single instance of GIC
 * This controller is used to co-ordinate how to keep the GIC modal in sync with the InProgressIssue
 * as well as the persistence for InProgressIssue
 */

export const actions = {
	onTriggerIssueCreateModalStateChange:
		(
			triggerIssueCreateModalState: TriggerIssueCreateModalState,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();

			if (
				!(currentState.id || triggerIssueCreateModalState.inProgressIssueID) ||
				!(
					currentState?.triggerIssueCreateModalState?.isModalOpen ||
					triggerIssueCreateModalState?.isModalOpen
				)
			) {
				return;
			}

			// Preventing Project, Issue type and Request type from being updated
			const overridePayload = {
				project: currentState?.triggerIssueCreateModalState?.payload?.project,
				issueType: currentState?.triggerIssueCreateModalState?.payload?.issueType,
				requestType: currentState?.triggerIssueCreateModalState?.payload?.requestType,
			};

			const newPayload = {
				...(triggerIssueCreateModalState.payload || {}),
				...overridePayload,
			};

			setState({
				id: triggerIssueCreateModalState.inProgressIssueID, // In case this changes somehow
				isSavingCounter: currentState.isSavingCounter + 1,
				triggerIssueCreateModalState: {
					...triggerIssueCreateModalState,
					payload: newPayload,
				},
			});

			const { updateInProgressIssue } = inProgressIssueService;
			const newState = getState();
			try {
				await updateInProgressIssue(newState.id, convertStateToInProgressIssueForStorage(newState));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onTriggerIssueCreateModalStateChange',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});

				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onFormFieldUpdate:
		({
			fieldId,
			value,
		}: CREATE_FORM_FIELD_VALUE_CHANGE_PAYLOAD): Action<
			InProgressIssueControllerState,
			ContainerProps,
			Promise<void>
		> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics(`onFormFieldUpdate - ${fieldId}`, currentState);
				return;
			}
			const newFormData = { ...currentState.formData, [fieldId]: value };
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				formData: newFormData,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(getState()),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onFormFieldUpdate',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			// Resetting is saving counter should be delayed until after throttle
			// execution to allow us to show a prompt if page unloads in between
			// invocations
			setTimeout(() => {
				setState({
					isSavingCounter: getState().isSavingCounter - 1,
				});
			}, THROTTLE_DELAY + 1);
		},
	onClassificationLevelFieldChange:
		(
			classificationLevelField: ClassificationLevelFieldChangePayload,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onClassificationLevelFieldChange', currentState);
				return;
			}
			setState({
				isSavingCounter: getState().isSavingCounter + 1,
				classificationLevelField,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			const newState = getState();
			try {
				await updateInProgressIssue(newState.id, convertStateToInProgressIssueForStorage(newState));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onClassificationLevelFieldChange',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onStatusFieldChange:
		(
			statusField: StatusFieldChangePayload,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onStatusFieldChange', currentState);
				return;
			}

			setState({
				isSavingCounter: getState().isSavingCounter + 1,
				statusField,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			const newState = getState();
			try {
				await updateInProgressIssue(newState.id, convertStateToInProgressIssueForStorage(newState));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onStatusFieldChange',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onFormContextUpdate:
		(
			formContext: UseFormContextStateChange,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();

			const { id, triggerIssueCreateModalState, isSavingCounter } = currentState;

			if (!id || !triggerIssueCreateModalState || !triggerIssueCreateModalState.isModalOpen) {
				return;
			}
			const currentFormContext = currentState.formContext;
			const newFormContext = formContext;

			if (triggerIssueCreateModalState && !isEqual(newFormContext, currentFormContext)) {
				const currentPayload =
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					triggerIssueCreateModalState.payload as TriggerIssueCreateModalState;

				const selectedIssueAndRequestType = newFormContext.selectedIssueType
					? {
							issueType: {
								issueTypeId: isRequestType(newFormContext.selectedIssueType)
									? newFormContext.selectedIssueType.issueTypeId
									: newFormContext.selectedIssueType?.value,
							},
							requestType: isRequestType(newFormContext.selectedIssueType)
								? { key: newFormContext.selectedIssueType.requestTypeKey }
								: undefined,
						}
					: undefined;

				const newTriggerIssueCreateModalState = {
					...triggerIssueCreateModalState,
					payload: {
						...currentPayload,
						project: { projectId: newFormContext.selectedProject?.value },
						...selectedIssueAndRequestType,
					},
				};

				const newState = {
					isSavingCounter: isSavingCounter + 1,
					formContext: newFormContext,
					triggerIssueCreateModalState: newTriggerIssueCreateModalState,
				};

				setState(newState);

				const { updateInProgressIssue } = inProgressIssueService;
				try {
					await updateInProgressIssue(id, convertStateToInProgressIssueForStorage(getState()));
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'useInProgressIssueService',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onFormContextUpdate',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
					});
					setState({ restoreError: error });
				}
				setState({
					isSavingCounter: getState().isSavingCounter - 1,
				});
			}
		},
	onModalOpened:
		({
			inProgressIssueID,
		}: {
			inProgressIssueID: string;
		}): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const { createInProgressIssue } = inProgressIssueService;
			const newState = {
				...getState(),
				id: inProgressIssueID,
			};
			setState(newState);
			try {
				await createInProgressIssue(
					inProgressIssueID,
					convertStateToInProgressIssueForStorage(newState),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onModalOpened',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
		},
	onModalClosed:
		({
			inProgressIssueID,
		}: {
			inProgressIssueID: string;
		}): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			if (inProgressIssueID === getState().id) {
				setState({ ...initialState, isInitialised: true, restoreError: undefined });
				const { deleteInProgressIssue } = inProgressIssueService;
				try {
					await deleteInProgressIssue(inProgressIssueID);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'useInProgressIssueService',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onModalClosed',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
					});
					setState({ restoreError: error });
				}
			}
		},
	onModalDiscardExistingInProgressIssue:
		(): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const state = getState();
			setState({
				...initialState,
				isSavingCounter: state.isSavingCounter + 1,
				id: state.id,
				triggerIssueCreateModalState: state.triggerIssueCreateModalState,
				formContext: state.formContext,
				isInitialised: true,
				restoreError: undefined,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(state.id, convertStateToInProgressIssueForStorage(getState()));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onModalDiscardExistingInProgressIssue',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onResetFormFieldsForCreateAnother:
		(
			fieldsReset: ResetFormFieldsForCreateAnotherPayload,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onResetFormFieldsForCreateAnother', currentState);
				return;
			}

			const newFormData = { ...currentState.formData, ...fieldsReset.fieldsReset };
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				formData: newFormData,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(getState()),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onResetFormFieldsForCreateAnother',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onReinsertIntoBrowserStorage:
		(): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onReinsertIntoBrowserStorage', currentState);
				return;
			}

			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
			});
			const { createInProgressIssue } = inProgressIssueService;
			try {
				await createInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(currentState),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onReinsertIntoBrowserStorage',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	init:
		(
			inProgressIssueID?: string,
		): Action<
			InProgressIssueControllerState,
			ContainerProps,
			Promise<InProgressIssueControllerState>
		> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			if (inProgressIssueID) {
				// Get useTriggerIssueCreateModalState from storage
				const { getInProgressIssue } = inProgressIssueService;
				try {
					const inProgressIssue = await getInProgressIssue(inProgressIssueID);
					setState({
						...inProgressIssue,
						triggerIssueCreateModalState: inProgressIssue?.triggerIssueCreateModalState
							? {
									...inProgressIssue?.triggerIssueCreateModalState,
									wasStateRestored: true,
								}
							: undefined,
						isInitialised: true,
					});
				} catch (error) {
					setState({ restoreError: error });
				}
				return getState();
			}
			setState({
				isInitialised: true,
			});
			return getState();
		},
	onFormFieldConfigUpdate:
		({
			fieldConfig,
		}: CreateFormFieldConfigUpdatePayload): Action<
			InProgressIssueControllerState,
			ContainerProps,
			Promise<void>
		> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState.id) {
				return;
			}
			let newFormDataDefaultValues = {};

			Object.values(fieldConfig).forEach((fieldData) => {
				const { fieldId, defaultValue } = fieldData;
				newFormDataDefaultValues = {
					...newFormDataDefaultValues,
					[fieldId]: defaultValue,
				};
			});
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				formDataDefaultValues: newFormDataDefaultValues,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(getState()),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onFormFieldConfigUpdate',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onIssueTypeChange:
		(
			_: IssueTypeFieldValueChangePayload,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onIssueTypeChange', currentState);
				return;
			}

			// reset form fields default values
			const newFormDataDefaultValues = {};
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				formDataDefaultValues: newFormDataDefaultValues,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(getState()),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onIssueTypeChange',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	onProjectFieldChange:
		(
			_: ProjectFieldValueChangePayload,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { inProgressIssueService }) => {
			const currentState = getState();
			if (!currentState?.id || !currentState?.triggerIssueCreateModalState?.isModalOpen) {
				fireUnexpectedEventErrorAnalytics('onProjectFieldChange', currentState);
				return;
			}

			// reset form fields default values
			const newFormDataDefaultValues = {};
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				formDataDefaultValues: newFormDataDefaultValues,
			});
			const { updateInProgressIssue } = inProgressIssueService;
			try {
				await updateInProgressIssue(
					currentState.id,
					convertStateToInProgressIssueForStorage(getState()),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInProgressIssueService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onProjectFieldChange',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
				});
				setState({ restoreError: error });
			}
			setState({
				isSavingCounter: getState().isSavingCounter - 1,
			});
		},
	setRestoreError:
		(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			restoreError: any,
		): Action<InProgressIssueControllerState, ContainerProps, Promise<void>> =>
		async ({ setState }) => {
			setState({
				restoreError,
			});
		},
};

type Actions = typeof actions;

const Store = createStore<InProgressIssueControllerState, Actions>({ initialState, actions });
export const useInProgressIssue = createHook(Store);

export const InProgressIssueStoreContainer = createContainer<
	InProgressIssueControllerState,
	Actions,
	ContainerProps
>(Store);

export const useInProgressIssueActions = createActionsHook(Store);

// Selectors
const getIsFormDirty = (state: InProgressIssueControllerState) => state?.formContext?.isFormDirty;
export const useIsFormDirty = createHook(Store, {
	selector: getIsFormDirty,
});

const getIsModalOpen = (state: InProgressIssueControllerState) =>
	Boolean(state?.id && state?.triggerIssueCreateModalState?.isModalOpen);
export const useIsModalOpen = createHook(Store, {
	selector: getIsModalOpen,
});
